'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import { Plot, Polyline, Seg, Txt } from '../_components/widgets/Plot'
import * as EX from './exercises/neural-nets'

const OptimizerRace = dynamic(() => import('../_components/widgets/NNWidgets').then(m => m.OptimizerRace), { ssr: false })

function Schedule() {
  const total = 100
  const warm = 8
  const lr = (t: number) => (t < warm ? t / warm : 0.5 * (1 + Math.cos((Math.PI * (t - warm)) / (total - warm))))
  return (
    <Plot x={[0, total]} y={[0, 1.1]} height={200} axes="box" xLabel="training progress" yLabel="learning rate" ariaLabel="Warmup then cosine decay">
      <Polyline points={Array.from({ length: 101 }, (_, t) => [t, lr(t)] as [number, number])} color="green" width={2.6} />
      <Seg x1={warm} y1={0} x2={warm} y2={1.05} color="var(--gd-ink-3)" dashed width={1} />
      <Txt x={warm} y={1.05} dx={4} size={11} color="ink">
        warm-up ends
      </Txt>
    </Plot>
  )
}

export default function Optimizers() {
  return (
    <Lesson>
      <Step>
        <H>Plain gradient descent has problems</H>
        <P>
          Real loss landscapes aren’t neat bowls. They have long narrow <strong>ravines</strong> — steep in one direction,
          nearly flat in another. Gradient descent bounces between the steep walls while barely creeping along the floor. One
          learning rate can’t suit both directions: big enough to make progress along the floor means too big for the walls.
        </P>
        <P>A handful of smarter update rules — <strong>optimizers</strong> — fix this.</P>
      </Step>

      <Step gate="q-momentum">
        <H>Momentum: a heavy ball</H>
        <P>
          Instead of stepping in the gradient’s direction, keep a running <strong>velocity</strong> that accumulates past
          gradients. Consistent directions build up speed; back-and-forth zig-zags cancel out.
        </P>
        <MB>{'\\mathbf{v} \\leftarrow \\beta\\,\\mathbf{v} + \\pink{\\nabla L} \\qquad \\green{\\mathbf{w}} \\leftarrow \\green{\\mathbf{w}} - \\eta\\,\\mathbf{v}'}</MB>
        <P>
          <M>{'\\beta'}</M> (typically 0.9) controls how much of the old velocity is kept — like friction.
        </P>
        <Quiz
          id="q-momentum"
          question="In a ravine, why does momentum help?"
          options={[
            { label: 'The side-to-side gradients keep flipping sign and cancel; the along-the-floor ones agree and add up.', correct: true },
            { label: 'It uses a bigger learning rate.', why: 'Same learning rate — the velocity just averages the directions.' },
            { label: 'It computes the exact minimum.', why: 'It’s still an iterative method; it just takes a smarter path.' },
          ]}
        />
      </Step>

      <Step>
        <H>Adaptive steps: RMSProp and Adam</H>
        <P>
          Another idea: give <em>each parameter its own step size</em>. Keep a running average of each gradient’s squared size;
          divide the step by its square root. Parameters with huge, noisy gradients take small careful steps; parameters with
          tiny gradients take bigger ones. That’s <strong>RMSProp</strong>.
        </P>
        <P>
          <strong>Adam</strong> combines both: momentum for the direction, RMSProp-style scaling for the size. It’s the default
          optimizer for most deep learning, including large language models.
        </P>
        <MB>{'\\mathbf{m} \\leftarrow \\beta_1 \\mathbf{m} + (1-\\beta_1)\\pink{\\mathbf{g}} \\qquad \\mathbf{v} \\leftarrow \\beta_2 \\mathbf{v} + (1-\\beta_2)\\pink{\\mathbf{g}}^2 \\qquad \\green{\\mathbf{w}} \\leftarrow \\green{\\mathbf{w}} - \\eta \\frac{\\hat{\\mathbf{m}}}{\\sqrt{\\hat{\\mathbf{v}}} + \\epsilon}'}</MB>
        <Figure n="7.9" wide caption="Four optimizers race down a ravine from the same start toward the star. Watch plain SGD zig-zag, momentum overshoot and settle, and the adaptive methods head more directly along the floor. Toggle racers by tapping their names.">
          <OptimizerRace />
        </Figure>
      </Step>

      <Step gate="optimizers">
        <H>Implement momentum and Adam</H>
        <Exercise
          id="optimizers"
          title="Beat plain SGD"
          {...EX.optimizers}
          hints={[
            <>
              Momentum loop: get <C>[gx, gy]</C>, then <C>vx = beta * vx + gx</C> (same for y), then <C>x -= lr * vx</C>.
            </>,
            <>
              Adam: loop <C>t</C> from 1. Update m and v, compute the bias-corrected <C>m / (1 - 0.9 ** t)</C> and{' '}
              <C>v / (1 - 0.999 ** t)</C>, then step by <C>lr * mHat / (Math.sqrt(vHat) + 1e-8)</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-schedule">
        <H>Learning-rate schedules</H>
        <P>
          The learning rate doesn’t have to stay fixed. Big models are typically trained with a short <strong>warm-up</strong>{' '}
          (start tiny, ramp up, so the random initial weights don’t get blown apart) and then a slow <strong>decay</strong> (take
          smaller, more careful steps as you approach the bottom).
        </P>
        <Figure n="7.10" caption="Warm-up followed by cosine decay — the schedule used to train many large language models.">
          <Schedule />
        </Figure>
        <Quiz
          id="q-schedule"
          question="Why lower the learning rate near the end of training?"
          options={[
            { label: 'Near the bottom, big steps overshoot back and forth; small steps let it settle into the minimum.', correct: true },
            { label: 'To save electricity.', why: 'Each step costs the same compute regardless of the learning rate.' },
            { label: 'Gradients get larger near the end.', why: 'They usually get smaller — but the noise from mini-batches stays, and big steps amplify it.' },
          ]}
        />
        <Callout kind="why">
          You’ve now built neural networks from scratch: neurons, layers, backpropagation, autograd, training loops and modern
          optimizers. Next module, we point all of this at the most exciting data of all — language.
        </Callout>
      </Step>
    </Lesson>
  )
}
